import { getDefaultStore } from 'jotai'
import type { NavigateFunction } from 'react-router'
import { Experiments } from 'src/experiments/experiments'
import { attemptParseJSON } from 'src/utils'
import {
  getAllVariants,
  reportExperimentExposure,
} from 'src/utils/experimentNew'
import { stripLocalePrefix } from 'src/utils/router/route'
import { openNewUserDailyRewardsDialog } from './components/NewUserDailyRewardsDialog'
import { openNewUserOnboardingDialog as openTreatmentNewUserOnboardingDialog } from './components/NewUserOnboardingDialog'
import { onboardingPendingAtom } from './states'

export async function openNewUserOnboardingDialog(options?: {
  navigate?: NavigateFunction
}) {
  const variantKey = Experiments.OnboardingInGenerationPanel
  const variant = await resolveVariant(variantKey)
  // Only bucketed users count as exposed. Amplitude returns 'off' for
  // non-participants and resolveVariant yields undefined when the fetch fails;
  // exposing either would pollute the experiment population (mirrors the guard
  // in useExperimentExposure).
  if (variant === 'control' || variant === 'treatment') {
    reportExperimentExposure(variantKey)
  }

  // Treatment: route to the generator first so the dialogs appear over the
  // real product surface. We can't open them here and navigate — the dialog
  // container lives inside RootRuntimeInner's <Suspense>, so the route
  // transition orphans any dialog opened beforehand. Instead defer: flag it
  // and let OnboardingPendingWatcher reopen once the generator is mounted.
  const onGenerator = stripLocalePrefix(window.location.pathname).startsWith(
    '/generator/',
  )
  if (variant === 'treatment' && !onGenerator && options?.navigate) {
    getDefaultStore().set(onboardingPendingAtom, true)
    options.navigate('/generator/image')
    return
  }

  await runOnboardingDialogs()
}

// The daily-rewards → onboarding-steps sequence, with no routing concerns.
// Called directly when already on the right surface, or by
// OnboardingPendingWatcher after the deferred redirect lands.
export async function runOnboardingDialogs() {
  await openNewUserDailyRewardsDialog()
  await openTreatmentNewUserOnboardingDialog()
}

// Mirrors useExperimentVariant's resolution: localStorage override (set by
// the DebugPanel FeatureFlag row) wins; otherwise await the Amplitude fetch.
// Without this, toggling the variant in the debug widget doesn't affect the
// non-hook redirect path.
async function resolveVariant(name: string): Promise<string | undefined> {
  const local = attemptParseJSON(localStorage.getItem(`exp:${name}`))
  if (typeof local === 'string' && local !== '') return local
  try {
    const variants = await getAllVariants()
    return variants?.[name]?.value ?? undefined
  } catch {
    // Amplitude unreachable: fall back to control so onboarding still opens
    // for the new user instead of throwing and showing nothing.
    return undefined
  }
}
